import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { leaveService } from "../services/leave.service";
import { useAuth } from "../hooks/useAuth";
import { dateStringBeToCe, dateStringCeToBe } from "../utils/dateUtils";
import { MainLayout } from "../components/layout";

export default function LeaveDetail() {
  // Get ID from URL path: /leaves/:id
  const pathParts = window.location.pathname.split("/");
  const id = pathParts[pathParts.length - 1];
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [approvalComments, setApprovalComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Edit form state
  const [editForm, setEditForm] = useState({
    startDate: "",
    endDate: "",
    isHalfDayStart: false,
    isHalfDayEnd: false,
    reason: "",
    contactDuringLeave: "",
    emergencyContact: "",
  });

  // Display state for Buddhist Era dates in edit form
  const [editDisplayDates, setEditDisplayDates] = useState({
    startDate: "",
    endDate: "",
  });

  // Fetch leave request details
  const {
    data: leaveRequest,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["leaveRequest", id],
    queryFn: () => leaveService.getLeaveRequest(id!),
    enabled: !!id,
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (comments?: string) =>
      leaveService.approveLeaveRequest(id!, comments),
    onSuccess: (data) => {
      // Update cache immediately with new data
      queryClient.setQueryData(["leaveRequest", id], data);
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      setShowApproveModal(false);
      setApprovalComments("");
      alert("อนุมัติคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (comments: string) =>
      leaveService.rejectLeaveRequest(id!, comments),
    onSuccess: (data) => {
      // Update cache immediately with new data
      queryClient.setQueryData(["leaveRequest", id], data);
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      setShowRejectModal(false);
      setRejectionReason("");
      alert("ปฏิเสธคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => leaveService.cancelLeaveRequest(id!),
    onSuccess: (data) => {
      // Update cache immediately with new data
      queryClient.setQueryData(["leaveRequest", id], data);
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      alert("ยกเลิกคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: (data: typeof editForm) =>
      leaveService.updateLeaveRequest(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaveRequest", id] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      setShowEditModal(false);
      alert("แก้ไขคำขอลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  const handleApprove = () => {
    approveMutation.mutate(approvalComments || undefined);
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      alert("กรุณาระบุเหตุผลในการปฏิเสธ");
      return;
    }
    rejectMutation.mutate(rejectionReason);
  };

  const handleCancel = () => {
    if (window.confirm("คุณแน่ใจหรือไม่ที่จะยกเลิกคำขอลานี้?")) {
      cancelMutation.mutate();
    }
  };

  const handleOpenEditModal = () => {
    if (leaveRequest) {
      const startCE = leaveRequest.startDate.split("T")[0];
      const endCE = leaveRequest.endDate.split("T")[0];

      setEditForm({
        startDate: startCE,
        endDate: endCE,
        isHalfDayStart: leaveRequest.isHalfDayStart || false,
        isHalfDayEnd: leaveRequest.isHalfDayEnd || false,
        reason: leaveRequest.reason,
        contactDuringLeave: leaveRequest.contactDuringLeave || "",
        emergencyContact: leaveRequest.emergencyContact || "",
      });

      // Convert CE dates to BE for display
      setEditDisplayDates({
        startDate: dateStringCeToBe(startCE),
        endDate: dateStringCeToBe(endCE),
      });

      setShowEditModal(true);
    }
  };

  const handleEditSubmit = () => {
    // Validation
    if (!editForm.startDate || !editForm.endDate) {
      alert("กรุณาระบุวันที่เริ่มต้นและสิ้นสุด");
      return;
    }
    if (!editForm.reason.trim()) {
      alert("กรุณาระบุเหตุผลการลา");
      return;
    }
    if (new Date(editForm.startDate) > new Date(editForm.endDate)) {
      alert("วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด");
      return;
    }

    editMutation.mutate(editForm);
  };

  // Check if user can approve/reject (admin, HR, manager)
  const canApprove = user && ["admin", "hr", "manager"].includes(user.role);
  const isOwner = user && leaveRequest && user.id === leaveRequest.userId;
  const isPending = leaveRequest?.status === "pending";
  const isAdminOrHR = user && ["admin", "hr"].includes(user.role);

  // Owner can edit their own pending requests (that haven't started yet)
  // Admin/HR can edit pending requests only
  const canEdit =
    isPending &&
    leaveRequest &&
    new Date(leaveRequest.startDate) > new Date() &&
    (isOwner || isAdminOrHR);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !leaveRequest) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          ไม่พบข้อมูลคำขอลา
        </div>
        <button
          onClick={() => window.history.back()}
          className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800"
        >
          ← กลับ
        </button>
      </div>
    );
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "approved":
        return "อนุมัติ";
      case "rejected":
        return "ปฏิเสธ";
      case "cancelled":
        return "ยกเลิก";
      case "pending":
        return "รอพิจารณา";
      default:
        return status;
    }
  };

  return (
    <MainLayout title="รายละเอียดคำขอลา">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                รายละเอียดคำขอลา
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                รหัส: {leaveRequest.id}
              </p>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadgeColor(leaveRequest.status)}`}
            >
              {getStatusText(leaveRequest.status)}
            </span>
          </div>
        </div>

        {/* Leave Request Info */}
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">
            ข้อมูลคำขอลา
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-600">
                ประเภทการลา
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {leaveRequest.leaveType?.icon} {leaveRequest.leaveType?.name}
              </p>
            </div>

            {leaveRequest.user && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  ผู้ขอลา
                </label>
                <p className="text-lg text-gray-900 mt-1">
                  {leaveRequest.user.firstName} {leaveRequest.user.lastName}
                </p>
                <p className="text-sm text-gray-600">
                  {leaveRequest.user.email}
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-600">
                วันที่เริ่มต้น
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {new Date(leaveRequest.startDate).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                วันที่สิ้นสุด
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {new Date(leaveRequest.endDate).toLocaleDateString("th-TH", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                จำนวนวัน
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {leaveRequest.totalDays} วัน
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600">
                วันที่ยื่นคำขอ
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {new Date(leaveRequest.submittedAt).toLocaleDateString(
                  "th-TH",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-600">
                เหตุผลการลา
              </label>
              <p className="text-lg text-gray-900 mt-1">
                {leaveRequest.reason}
              </p>
            </div>

            {leaveRequest.contactDuringLeave && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  ช่องทางติดต่อระหว่างลา
                </label>
                <p className="text-lg text-gray-900 mt-1">
                  {leaveRequest.contactDuringLeave}
                </p>
              </div>
            )}

            {leaveRequest.emergencyContact && (
              <div>
                <label className="text-sm font-medium text-gray-600">
                  ผู้ติดต่อฉุกเฉิน
                </label>
                <p className="text-lg text-gray-900 mt-1">
                  {leaveRequest.emergencyContact}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {isPending && (
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              การดำเนินการ
            </h3>
            <div className="flex flex-wrap gap-3">
              {canApprove && (
                <>
                  <button
                    onClick={() => setShowApproveModal(true)}
                    disabled={approveMutation.isPending}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {approveMutation.isPending
                      ? "กำลังอนุมัติ..."
                      : "✓ อนุมัติ"}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={rejectMutation.isPending}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {rejectMutation.isPending ? "กำลังปฏิเสธ..." : "✗ ปฏิเสธ"}
                  </button>
                </>
              )}
              {canEdit && (
                <button
                  onClick={handleOpenEditModal}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  ✏️ แก้ไขคำขอ
                </button>
              )}
              {(isOwner || canApprove) && (
                <button
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                >
                  {cancelMutation.isPending
                    ? "กำลังยกเลิก..."
                    : "🗑️ ยกเลิกคำขอ"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {showApproveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">อนุมัติคำขอลา</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ความคิดเห็น (ไม่บังคับ)
                </label>
                <textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows={3}
                  placeholder="เพิ่มความคิดเห็น..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setApprovalComments("");
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  disabled={approveMutation.isPending}
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {approveMutation.isPending
                    ? "กำลังอนุมัติ..."
                    : "ยืนยันการอนุมัติ"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold mb-4 text-red-600 dark:text-red-400">
                ปฏิเสธคำขอลา
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  เหตุผลในการปฏิเสธ <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows={3}
                  placeholder="กรุณาระบุเหตุผลในการปฏิเสธ..."
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason("");
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  disabled={rejectMutation.isPending}
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending || !rejectionReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {rejectMutation.isPending
                    ? "กำลังปฏิเสธ..."
                    : "ยืนยันการปฏิเสธ"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full my-8">
              <h3 className="text-xl font-semibold mb-4 text-blue-600 dark:text-blue-400">
                ✏️ แก้ไขคำขอลา
              </h3>

              <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่เริ่มต้น <span className="text-red-500">*</span>
                      {isAdminOrHR && (
                        <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                          (สามารถเลือกวันที่ย้อนหลังได้)
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      value={editDisplayDates.startDate}
                      onChange={(e) => {
                        const ceDate = dateStringBeToCe(e.target.value);
                        setEditForm({ ...editForm, startDate: ceDate });
                        setEditDisplayDates({
                          ...editDisplayDates,
                          startDate: e.target.value,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                    />
                    {editDisplayDates.startDate && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        พ.ศ. {editDisplayDates.startDate.split("-")[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      วันที่สิ้นสุด <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editDisplayDates.endDate}
                      onChange={(e) => {
                        const ceDate = dateStringBeToCe(e.target.value);
                        setEditForm({ ...editForm, endDate: ceDate });
                        setEditDisplayDates({
                          ...editDisplayDates,
                          endDate: e.target.value,
                        });
                      }}
                      min={editDisplayDates.startDate}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      required
                    />
                  </div>
                </div>

                {/* Backdated Leave Warning */}
                {isAdminOrHR &&
                  editForm.startDate &&
                  new Date(editForm.startDate) <
                    new Date(new Date().setHours(0, 0, 0, 0)) && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded-lg">
                      <p className="text-sm flex items-center">
                        <span className="text-lg mr-2">⚠️</span>
                        <span>
                          <strong>คำเตือน:</strong>{" "}
                          คุณกำลังแก้ไขคำขอลาให้เป็นวันที่ย้อนหลัง
                        </span>
                      </p>
                    </div>
                  )}

                {/* Half Day Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editHalfDayStart"
                      checked={editForm.isHalfDayStart}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          isHalfDayStart: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="editHalfDayStart"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      ลาครึ่งวันแรก
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editHalfDayEnd"
                      checked={editForm.isHalfDayEnd}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          isHalfDayEnd: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="editHalfDayEnd"
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      ลาครึ่งวันสุดท้าย
                    </label>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    เหตุผลการลา <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editForm.reason}
                    onChange={(e) =>
                      setEditForm({ ...editForm, reason: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                    placeholder="ระบุเหตุผลการลา..."
                    required
                  />
                </div>

                {/* Contact During Leave */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ช่องทางติดต่อระหว่างลา
                  </label>
                  <input
                    type="text"
                    value={editForm.contactDuringLeave}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        contactDuringLeave: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="เบอร์โทร, อีเมล, หรือช่องทางอื่นๆ"
                  />
                </div>

                {/* Emergency Contact */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ผู้ติดต่อฉุกเฉิน
                  </label>
                  <input
                    type="text"
                    value={editForm.emergencyContact}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        emergencyContact: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="ชื่อและเบอร์ติดต่อผู้ที่สามารถติดต่อได้"
                  />
                </div>

                {/* Warning for Admin/HR */}
                {isAdminOrHR && !isOwner && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      ⚠️ คุณกำลังแก้ไขคำขอลาของพนักงานคนอื่น ในฐานะ{" "}
                      {user?.role === "admin" ? "Admin" : "HR"}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  disabled={editMutation.isPending}
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={editMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {editMutation.isPending ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
